import type { TFunction } from 'i18next';
import { flow } from 'lodash-es';
import type { FC, ReactElement } from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import { AuthActions } from 'common/auth/actions';
import { Form } from 'common/component/ValidationFormAndField';
import withTranslate from 'common/i18n/i18n';
import { mustMatch, required, validPassword } from 'common/utils/validations';
import { PasswordValidIndicators } from 'common/viewcomp/display/PasswordValidIndicators';
import { CITextField } from 'common/viewcomp/forms/fields/text';

interface Props {
    t: TFunction;
    onSubmit: () => void;
    submitting: boolean;
    children: (disableButton: boolean, submitButtonClass: string) => ReactElement;
    createPasswordRequest: (newpass: string) => void;
    delegatePasswordChange?: (newpass: string) => void;
    createPasswordLabel?: string;
    confirmPasswordLabel?: string;
}

interface CreatePasswordFormValues {
    newpass: string;
    confirmpass: string;
}

const CreatePasswordFormComponent: FC<Props> = (props): ReactElement => {
    const {
        t,
        onSubmit,
        submitting,
        createPasswordRequest,
        children,
        delegatePasswordChange,
        createPasswordLabel,
        confirmPasswordLabel,
    } = props;
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setConfirmShowPassword] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleSubmit = ({ newpass, confirmpass }: CreatePasswordFormValues): void => {
        if (delegatePasswordChange) {
            delegatePasswordChange(newpass);
        } else {
            createPasswordRequest(newpass);
        }
        onSubmit();
    };

    const handleClickShowPassword = (): void => {
        setShowPassword(!showPassword);
    };

    const handleShowConfirmPassword = (): void => {
        setConfirmShowPassword(!showConfirmPassword);
    };

    return (
        <Form
            t={t}
            onSubmit={handleSubmit}
            initialValues={{ newpass: '', confirmpass: '' }}
            validations={[mustMatch('newpass', 'confirmpass', 'confirmpass', 'password_form.not_match')]}
            render={({ handleSubmit, invalid, values: { newpass } }): ReactElement => {
                const disableButton = invalid;
                const submitButtonClass = !submitting
                    ? 'submit_button disabled-submit-button'
                    : 'submit_button enabled-submit-button';

                return (
                    <form id="updatePassForm" onSubmit={handleSubmit}>
                        <CITextField
                            t={t}
                            id="newpass"
                            name="newpass"
                            labelI18NKey={createPasswordLabel || 'password_form.newpass'}
                            type={showPassword ? 'text' : 'password'}
                            margin="normal"
                            fullWidth
                            trimWhiteSpaces={false}
                            maxLength={16} // temporary until specs are defined
                            validations={[required(), validPassword('error.invalid_password')]}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={t('login.toggle_password')}
                                        onClick={handleClickShowPassword}
                                        size="large"
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {newpass && <PasswordValidIndicators password={newpass} t={t} />}
                        <CITextField
                            t={t}
                            id="confirmpass"
                            name="confirmpass"
                            labelI18NKey={confirmPasswordLabel || 'password_form.confirm_pass'}
                            type={showConfirmPassword ? 'text' : 'password'}
                            margin="normal"
                            trimWhiteSpaces={false}
                            fullWidth
                            maxLength={16} // temporary until specs are defined
                            validations={[required()]}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={t('login.toggle_password')}
                                        onClick={handleShowConfirmPassword}
                                        size="large"
                                    >
                                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />

                        {/* Childrens are buttons to be rendered with the this component
                        this makes button rendering more flexible, naming and maybe cancel button etc etc */}
                        {children(disableButton, submitButtonClass)}
                    </form>
                );
            }}
        />
    );
};

const mapDispatchToProps = { ...AuthActions };

const CreatePasswordForm = flow([withTranslate('auth')])(
    connect(null, mapDispatchToProps)(CreatePasswordFormComponent)
);

export default CreatePasswordForm;
